import {CircularProgress, DialogContent, DialogTitle, Grid} from "@mui/material";
import {Dialog2} from "../../misc/Dialog2";
import {SmoothActionBar} from "../../misc/SmoothActionBar";
import {useContext, useEffect, useMemo, useState} from "react";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../api/API";
import {Product} from "../../api/Products";
import {FindOrCreateCompany} from "../project/manage/summary/FindOrCreateCompany";
import {Company} from "../../api/Companies";
import {DimensionInput2} from "./DimensionInput2";
import {
    doorGauge,
    doorThicknesses
} from "../project/quote/openings/QuoteOpenings";
import {
    hollowMetalDoor, woodDoor
} from "../project/quote/openings/Const";
import {SelectFiltered} from "./SelectFiltered";
import {dummySelectWrapper} from "../../misc/scroller/Table";
import {doorBuilderPreps, StandardPrepSelector} from "./StandardPrepSelector";
import {DropDownContext, useDropdownData} from "../project/settings/DropDowns";

export function DoorBuilder(props: {
    onDone(v: Product): void;
    onCancel(): void;
    simpleQuoteMode?: boolean;
}) {
    const [core, setCore] = useState("");
    const [elevation] = useState("SLAB"); // always slab
    const [finish, setFinish] = useState("");
    const [gauge, setGauge] = useState("");
    const [handing, setHanding] = useState("");
    const [height, setHeight] = useState("");
    const [width, setWidth] = useState("");
    const [label] = useState(""); // always blank
    const [material, setMaterial] = useState("");
    const [series, setSeries] = useState("");
    const [thickness, setThickness] = useState("");
    const [seam, setSeam] = useState("");

    const act = useAsyncAction(async (input) => {
        const p = await api.products.buildDoorSpec(input)
        props.onDone(p)
    }, [])

    const [company, setCompany] = useState<Company|null>(null);
    const [preps, setPreps] = useState<string[]>([]);

    const companySearch = useAsync2(() => api.companies.list({kind: "supplier", search: "Tykel", offset: 0}), {}, [])
    const tykelCos = companySearch.result?.data;
    useEffect(() => {
        if(tykelCos?.length) {
            setCompany(tykelCos[0])
        }
    }, [tykelCos]);

    const sys = useMemo(() => {
        if(width.toLowerCase().trim().endsWith("mm")) {
            return "metric"
        }

        return undefined;
    }, [width]);

    const ddl = useContext(DropDownContext);
    const doorMaterials = useDropdownData(ddl.doorMaterial);
    const doorFinish = useDropdownData(ddl.doorMaterialWoodFinish);
    const doorCore = useDropdownData(ddl.doorCores);
    const doorSeam = useDropdownData(ddl.doorSeam);

    if(ddl.loading) {
        return <Dialog2 open>
            <CircularProgress />
        </Dialog2>
    }

    const submit = () => {
        act.callback({
            manufacturer: company?.id || 0,
            core,
            elevation,
            finish,
            gauge,
            handing,
            height,
            width,
            label,
            material,
            series,
            thickness,
            seam,
            preps
        })
    };

    return (
        <Dialog2 open onClose={props.onCancel} onEnterPress={submit}>
            <DialogTitle>Door Builder</DialogTitle>
            <DialogContent>
                <div style={{height: 8}}/>
                <FindOrCreateCompany value={company} kind={"supplier"} label={"Manufacturer Name"}
                                     onChange={setCompany}/>

                <div style={{height: 8}}/>

                <Grid container spacing={1} flexDirection="row">
                    <Grid item xs={4}>
                        <DimensionInput2 label="Width" value={width} onChange={setWidth}/>
                    </Grid>
                    <Grid item xs={4}>
                        <DimensionInput2 label="Height" value={height} onChange={setHeight}/>
                    </Grid>
                    {!props.simpleQuoteMode && <Grid item xs={4}>
                        <SelectFiltered label="Handing" value={handing} onChange={setHanding}
                                        options={["LH", "RH", "REV"].map(v => ({value: v, display: v}))}/>
                    </Grid>}
                </Grid>

                <div style={{height: 8}}/>

                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <SelectFiltered label="Series" value={series} onChange={setSeries}
                                        options={[hollowMetalDoor, woodDoor].map(v => ({value: v, display: v}))}/>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Material" value={material} onChange={setMaterial}
                                        options={doorMaterials.filter(d => d.doorSeries === series)}/>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Finish" value={finish} onChange={setFinish}
                                        options={doorFinish.filter(d => d.doorSeries === series)}/>
                    </Grid>
                </Grid>

                <div style={{height: 8}}/>

                <Grid container spacing={1} flexDirection="row">
                    <Grid item xs={4}>
                        <SelectFiltered label="Gauge" value={gauge} onChange={setGauge}
                                        options={doorGauge(dummySelectWrapper({doorSeries: series}))}/>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Thickness" value={thickness} onChange={setThickness}
                                        options={doorThicknesses(sys).map(v => ({value: v, display: v}))}/>
                    </Grid>
                </Grid>

                <div style={{height: 8}}/>

                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <SelectFiltered label="Core" value={core} onChange={setCore}
                                        options={doorCore.filter(d => d.doorSeries === series)} />
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Seam" value={seam} onChange={setSeam}
                                        options={doorSeam.filter(d => d.doorSeries === series)}/>
                    </Grid>
                </Grid>

                {!props.simpleQuoteMode && <>
                    <div style={{height: 8}}/>
                    <StandardPrepSelector optionList={doorBuilderPreps} preps={preps} onChange={setPreps} />
                </>}

            </DialogContent>
            <SmoothActionBar
                loading={act.loading}
                error={act.error}
                actionName="Confirm"
                onCancel={props.onCancel}
                onSubmit={submit}
            />
        </Dialog2>
    )
}